import React, { useEffect, useRef, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { css } from '@emotion/react';

import { ADMIN_ACCESS_TOKEN_KEY, getFetcher, SwButtonSection } from 'App';
import PrimaryButton from '@components/atoms/Inputs/Button/Primary';
import { dialogContentsState } from '@components/templates';
import SettingCommonMethods from 'utils/settingCommon';
import {
  FlexBox,
  StyledDuplicateDescription,
  StyledOnOffFlex,
  StyledReportArea,
  StyledReportFlex,
  StyledReportWrap,
} from './style';
import { submitDebounce } from '../../../../utils/lib';
import { Input, Switch } from '@components/atoms';
import _ from 'lodash';
import { InputBox } from '@pages/AppSetting/NM/Option/style';
import { REGEX_NUMBER } from '@variables/utils';

export interface CTPDefault {
  cbf: number;
  cbfColormapFlip: boolean;
  cbfThresholds: number[];
  cbvColormapFlip: boolean;
  checkDuplicate: boolean;
  mttColormapFlip: boolean;
  tmax: number;
  tmaxColormapFlip: boolean;
  tmaxMax: number;
  tmaxThresholds: number[];
  ttpColormapFlip: boolean;
  isColorMapAutoRange: boolean;
}

const Option = () => {
  const [accessToken, setAccessToken] = useState('');
  const [isModified, setIsModified] = useState(false);
  const [checkDuplicate, setCheckDuplicate] = useState(false);
  const [prevent, setPrevent] = useState(false); // apply버튼 데이터 받아온 후 동작되도록

  const [thresholdsTmax, setThresholdsTmax] = useState<string>('');
  const [thresholdsCbf, setThresholdsCbf] = useState<string>('');

  const [data, setData] = useState<CTPDefault>({
    cbf: 0,
    tmax: 0,
    tmaxMax: 0,
    cbfThresholds: [],
    tmaxThresholds: [],
    checkDuplicate: false,
    cbfColormapFlip: false,
    cbvColormapFlip: false,
    mttColormapFlip: false,
    tmaxColormapFlip: false,
    ttpColormapFlip: false,
    isColorMapAutoRange: false,
  });
  const [originData, setOriginData] = useState({ ...data });

  const [colorMapFlip, setColorMapFlip] = useState({
    cbfColormapFlip: data.cbfColormapFlip,
    cbvColormapFlip: data.cbvColormapFlip,
    mttColormapFlip: data.mttColormapFlip,
    tmaxColormapFlip: data.tmaxColormapFlip,
    ttpColormapFlip: data.ttpColormapFlip,
  });

  const isMounted = useRef(false); // useEffect 첫 실행 막기

  const { errorHandler } = SettingCommonMethods();
  const setDialogContents = useSetRecoilState(dialogContentsState);

  const colorMapOption = ['Low(Blue) - High(Red)', 'Low(Red) - High(Blue)'];

  /**
   * setting 데이터 가져오기
   */
  const getSettingData = async () => {
    const url = 'api/setting/ctp/option';
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          setData({ ...r.result });
          setOriginData({ ...r.result });

          setColorMapFlip({
            cbfColormapFlip: r.result.cbfColormapFlip,
            cbvColormapFlip: r.result.cbvColormapFlip,
            mttColormapFlip: r.result.mttColormapFlip,
            tmaxColormapFlip: r.result.tmaxColormapFlip,
            ttpColormapFlip: r.result.ttpColormapFlip,
          });
          setCheckDuplicate(r.result.checkDuplicate);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * 데이터 업데이트 api post
   */
  const updateNotificationSettings = async () => {
    const url = 'api/setting/ctp/option';

    const r = await getFetcher({
      method: 'POST',
      accessToken,
      body: data,
    })(url);

    if (r.status === 'SUCCESS' && r.code === '200') {
      setDialogContents({
        type: 'success',
        body: (
          <>
            <div
              style={{
                margin: '10px auto',
                textAlign: 'center',
              }}
            >
              Success!
            </div>
          </>
        ),
        footer: (
          <>
            <PrimaryButton
              onClick={async () => {
                setDialogContents({ type: 'hidden' });
              }}
            >
              OK
            </PrimaryButton>
          </>
        ),
      });

      setIsModified(false);
      setOriginData(data);
    } else {
      errorHandler(r);
    }
  };

  /**
   * 컬러맵
   * @param e
   */
  const changeSetting = (e: React.ChangeEvent<HTMLInputElement>) => {
    const _data = JSON.parse(JSON.stringify(data));

    const name = e.target.name;

    if (name === 'tmax') {
      _data.tmaxColormapFlip = !_data.tmaxColormapFlip;
      setColorMapFlip((prev) => ({ ...prev, tmaxColormapFlip: _data.tmaxColormapFlip }));
    } else if (name === 'CBF') {
      _data.cbfColormapFlip = !_data.cbfColormapFlip;
      setColorMapFlip((prev) => ({ ...prev, cbfColormapFlip: _data.cbfColormapFlip }));
    } else if (name === 'CBV') {
      _data.cbvColormapFlip = !_data.cbvColormapFlip;
      setColorMapFlip((prev) => ({ ...prev, cbvColormapFlip: _data.cbvColormapFlip }));
    } else if (name === 'MTT') {
      _data.mttColormapFlip = !_data.mttColormapFlip;
      setColorMapFlip((prev) => ({ ...prev, mttColormapFlip: _data.mttColormapFlip }));
    } else if (name === 'TTP') {
      _data.ttpColormapFlip = !_data.ttpColormapFlip;
      setColorMapFlip((prev) => ({ ...prev, ttpColormapFlip: _data.ttpColormapFlip }));
    }

    setData(_data);
  };

  /**
   * mismatch / tmaxColormap value valid
   */
  const inputValueValid = (type: string, action: string, value: string) => {
    let validValue = value.replaceAll(REGEX_NUMBER, '').replace(/^0+/, '');
    if (!validValue || Number(validValue) === 0) validValue = '';

    if (type === 'TMAX_AUTO') {
    } else if (type === 'TMAX' || type === 'TMAX_MAX') {
      if (action === 'BLUR') {
        if (!validValue) validValue = type === 'TMAX' ? String(originData.tmax) : String(originData.tmaxMax);
        else if (Number(validValue) < 1) validValue = '1';
        else if (Number(validValue) > 20) validValue = '20';
      }
    } else {
      if (action === 'BLUR') {
        if (!validValue) validValue = String(originData.cbf);
        if (Number(validValue) < 20) validValue = '20';
        else if (Number(validValue) > 50) validValue = '50';
      }
    }

    setData((prevState) => {
      const _tmp = JSON.parse(JSON.stringify(prevState));
      if (type === 'TMAX_AUTO') {
        _tmp.isColorMapAutoRange = value === 'true';
      } else if (type === 'TMAX') {
        _tmp.tmax = validValue;
      } else if (type === 'TMAX_MAX') {
        _tmp.tmaxMax = validValue;
      } else {
        _tmp.cbf = validValue;
      }
      return _tmp;
    });
  };

  /**
   * add thresholds
   */
  const addThresholds = (type: string) => {
    const _tmpData =
      type === 'TMAX'
        ? JSON.parse(JSON.stringify(data.tmaxThresholds))
        : JSON.parse(JSON.stringify(data.cbfThresholds));

    if ((type === 'TMAX' && data.tmaxThresholds.length >= 7) || (type !== 'TMAX' && data.cbfThresholds.length >= 7)) {
      setDialogContents({
        type: 'error',
        body: (
          <>
            <div
              style={{
                margin: '10px auto',
                textAlign: 'center',
              }}
            >
              Only up to seven can be registered.
            </div>
          </>
        ),
        footer: (
          <>
            <PrimaryButton
              onClick={async () => {
                setDialogContents({ type: 'hidden' });
              }}
            >
              OK
            </PrimaryButton>
          </>
        ),
      });
      return;
    }

    setData((prevState) => {
      const _tmp = JSON.parse(JSON.stringify(prevState));
      if (type === 'TMAX') {
        _tmpData.push(Number(thresholdsTmax));
        _tmp.tmaxThresholds = _tmpData;
      } else {
        _tmpData.push(Number(thresholdsCbf));
        _tmp.cbfThresholds = _tmpData;
      }
      return _tmp;
    });

    if (type === 'TMAX') {
      setThresholdsTmax('');
    } else {
      setThresholdsCbf('');
    }
  };

  /**
   * delete thresholds
   */
  const deleteThresholds = (type: string, value: number) => {
    const _tmpData =
      type === 'TMAX'
        ? JSON.parse(JSON.stringify(data.tmaxThresholds))
        : JSON.parse(JSON.stringify(data.cbfThresholds));

    const index = _tmpData.findIndex((tmax: number) => tmax === value);
    _tmpData.splice(index, 1);

    setData((prevState) => {
      const _tmp = JSON.parse(JSON.stringify(prevState));
      if (type === 'TMAX') {
        _tmp.tmaxThresholds = _tmpData;
      } else {
        _tmp.cbfThresholds = _tmpData;
      }
      return _tmp;
    });
  };

  /**
   * thresholds sort 반환
   * @param type
   */
  const sortThresholdsArr = (type: string) => {
    if (type === 'TMAX') {
      return data.tmaxThresholds.sort((a, b) => b - a);
    } else {
      return data.cbfThresholds.sort((a, b) => a - b);
    }
  };

  /**
   * thresholds value valid
   */

  const thresholdsValueValid = (type: string, action: string, value: string) => {
    let validValue = value.replaceAll(REGEX_NUMBER, '').replace(/^0+/, '');
    const min = type === 'TMAX' ? 1 : 20;
    const max = type === 'TMAX' ? 20 : 50;
    if (!validValue || Number(validValue) === 0) validValue = '';

    if (action === 'BLUR') {
      if (Number(validValue) < min) validValue = String(min);
      else if (Number(validValue) > max) validValue = String(max);
    }

    if (type === 'TMAX') {
      setThresholdsTmax(validValue);
    } else {
      setThresholdsCbf(validValue);
    }
  };

  /**
   * thresholds add button valid
   */

  const thresholdAddBtnVaild = (type: string) => {
    const targetData = type === 'TMAX' ? thresholdsTmax : thresholdsCbf;

    const min = type === 'TMAX' ? 1 : 20;
    const max = type === 'TMAX' ? 20 : 50;
    const hasSameAndMaxData =
      type === 'TMAX'
        ? data.tmaxThresholds.filter((tmax) => tmax === Number(thresholdsTmax)).length > 0
        : data.cbfThresholds.filter((cbf) => cbf === Number(thresholdsCbf)).length > 0;

    return !targetData || Number(targetData) < min || Number(targetData) > max || hasSameAndMaxData;
  };

  /**
   * 최초 호출 (기본 셋팅값)
   */
  useEffect(() => {
    (async () => {
      await getSettingData();
      setPrevent(true);
    })();
  }, []);

  /**
   * SET token key in state
   */
  useEffect(() => {
    const newToken = localStorage.getItem(ADMIN_ACCESS_TOKEN_KEY);
    if (newToken) setAccessToken(newToken);
  }, []);

  /**
   * 옵션값 변화 감지해서 Apply 활성화
   */
  useEffect(() => {
    if (!prevent) return; // 데이터 받아와야 동작되도록

    const _isSame = _.isEqual(data, originData);

    if (isMounted.current) {
      if (!_isSame) {
        setIsModified(true);
      } else {
        setIsModified(false);
      }
    } else {
      isMounted.current = true;
    }
  }, [colorMapFlip, prevent, data, originData]);

  return (
    <>
      <label>Option</label>
      <StyledReportWrap>
        <StyledReportArea>
          <strong>
            Default Value <br />
            (mismatch map)
          </strong>
          <StyledReportFlex>
            <FlexBox>
              <FlexBox>
                <span className="l-title">Tmax</span>
                <InputBox className={'l-input'}>
                  <Input
                    type="text"
                    value={data.tmax.toString()}
                    onChange={(ev) => inputValueValid('TMAX', 'CHANGE', ev.target.value)}
                    onBlur={(ev) => inputValueValid('TMAX', 'BLUR', ev.target.value)}
                  />
                </InputBox>
                <span className="suffix">s</span>
              </FlexBox>
              <FlexBox>
                <span className="l-title">CBF</span>
                <InputBox className={'l-input'}>
                  <Input
                    type="text"
                    value={data.cbf.toString()}
                    onChange={(ev) => inputValueValid('CBF', 'CHANGE', ev.target.value)}
                    onBlur={(ev) => inputValueValid('CBF', 'BLUR', ev.target.value)}
                  />
                </InputBox>
                <span className="suffix">%</span>
              </FlexBox>
            </FlexBox>
            <StyledDuplicateDescription>Input range : Tmax(1~20), CBF(20-50) / Only integer</StyledDuplicateDescription>
          </StyledReportFlex>
        </StyledReportArea>

        <StyledReportArea>
          <strong>
            Default Value <br />
            (Tmax colormap)
          </strong>
          <StyledReportFlex>
            <FlexBox>
              <FlexBox>
                <span className="l-title">Tmax (color map) </span>
                <Input
                  className={'tmax-input'}
                  type={`radio`}
                  name={'TmaxColormap'}
                  checked={data.isColorMapAutoRange}
                  onChange={(e) => {
                    inputValueValid('TMAX_AUTO', 'CHANGE', 'true');
                    //auto 일경우 max 기본값으로 변경
                    inputValueValid('TMAX_MAX', 'CHANGE', '20');
                  }}
                >
                  Auto
                </Input>
              </FlexBox>
              <FlexBox>
                <Input
                  className={'tmax-input'}
                  type={`radio`}
                  name={'TmaxColormap'}
                  checked={!data.isColorMapAutoRange}
                  onChange={(e) => inputValueValid('TMAX_AUTO', 'CHANGE', 'false')}
                >
                  Customize :
                </Input>
                <span className="tmax-input-title ">max</span>
                <InputBox className={'l-input'}>
                  <Input
                    type="text"
                    value={String(data.tmaxMax)}
                    disabled={data.isColorMapAutoRange}
                    onChange={(ev) => inputValueValid('TMAX_MAX', 'CHANGE', ev.target.value)}
                    onBlur={(ev) => inputValueValid('TMAX_MAX', 'BLUR', ev.target.value)}
                  />
                </InputBox>
                <span className="suffix">s</span>
              </FlexBox>
            </FlexBox>
            <StyledDuplicateDescription>Input range : 1~20 / Only integer</StyledDuplicateDescription>
          </StyledReportFlex>
        </StyledReportArea>

        <StyledReportArea>
          <strong>
            Default Value <br />
            (thresholds)
          </strong>
          <StyledReportFlex>
            <div>
              <FlexBox>
                <FlexBox>
                  <span className="l-title">Tmax overview</span>
                  {sortThresholdsArr('TMAX').map((tmax, index) => {
                    return (
                      <React.Fragment key={index}>
                        <span className={'delete-text'}>{tmax}s</span>
                        {data.tmaxThresholds.length > 1 && (
                          <button className={'delete-btn'} onClick={() => deleteThresholds('TMAX', tmax)} />
                        )}
                      </React.Fragment>
                    );
                  })}
                  <div className={'vertical-bar'} />
                  <InputBox className={'l-input'}>
                    <Input
                      type="text"
                      value={thresholdsTmax}
                      onChange={(ev) => thresholdsValueValid('TMAX', 'CHANGE', ev.target.value)}
                      onBlur={(ev) => thresholdsValueValid('TMAX', 'BLUR', ev.target.value)}
                    />
                  </InputBox>
                  <span className="suffix">s</span>
                  <button
                    className={'threshold-add-btn'}
                    disabled={thresholdAddBtnVaild('TMAX')}
                    onClick={submitDebounce(() => addThresholds('TMAX'))}
                  >
                    add
                  </button>
                </FlexBox>
              </FlexBox>
            </div>
            <div>
              <FlexBox>
                <FlexBox>
                  <span className="l-title">CBF overview</span>
                  {sortThresholdsArr('CBF').map((cbf, index) => {
                    return (
                      <React.Fragment key={index}>
                        <span className={'delete-text'}>{cbf}%</span>
                        {data.cbfThresholds.length > 1 && (
                          <button className={'delete-btn'} onClick={() => deleteThresholds('CBF', cbf)} />
                        )}
                      </React.Fragment>
                    );
                  })}
                  <div className={'vertical-bar'} />
                  <InputBox className={'l-input'}>
                    <Input
                      type="text"
                      value={thresholdsCbf}
                      onChange={(ev) => thresholdsValueValid('CBF', 'CHANGE', ev.target.value)}
                      onBlur={(ev) => thresholdsValueValid('CBF', 'BLUR', ev.target.value)}
                    />
                  </InputBox>
                  <span className="suffix">%</span>
                  <button
                    className={'threshold-add-btn'}
                    disabled={thresholdAddBtnVaild('CBF')}
                    onClick={submitDebounce(() => addThresholds('CBF'))}
                  >
                    add
                  </button>
                </FlexBox>
              </FlexBox>
            </div>

            <StyledDuplicateDescription>
              Input range : Tmax(1~20), CBF(20-50) / Only integer / Up to 7 can be set
            </StyledDuplicateDescription>
          </StyledReportFlex>
        </StyledReportArea>

        <StyledReportArea>
          <strong>Color map</strong>
          <StyledReportFlex>
            <div className="flex-box">
              <div className="color-map-title">Tmax(Color map)</div>
              <Input
                className={'has-example'}
                type={`radio`}
                name={'tmax'}
                value={'lowBlue'}
                checked={!colorMapFlip.tmaxColormapFlip}
                onChange={(e) => changeSetting(e)}
              >
                {colorMapOption[0]}
              </Input>
              <Input
                className={'has-example'}
                type={`radio`}
                name={'tmax'}
                value={'lowRed'}
                checked={colorMapFlip.tmaxColormapFlip}
                onChange={(e) => changeSetting(e)}
              >
                {colorMapOption[1]}
              </Input>
            </div>
            <div className="flex-box">
              <div className="color-map-title">CBF(Color map)</div>
              <Input
                className={'has-example'}
                type={`radio`}
                name={'CBF'}
                value={'lowBlue'}
                checked={!colorMapFlip.cbfColormapFlip}
                onChange={(e) => changeSetting(e)}
              >
                {colorMapOption[0]}
              </Input>
              <Input
                className={'has-example'}
                type={`radio`}
                name={'CBF'}
                value={'lowRed'}
                checked={colorMapFlip.cbfColormapFlip}
                onChange={(e) => changeSetting(e)}
              >
                {colorMapOption[1]}
              </Input>
            </div>
            <div className="flex-box">
              <div className="color-map-title">CBV</div>
              <Input
                className={'has-example'}
                type={`radio`}
                name={'CBV'}
                value={'lowBlue'}
                checked={!colorMapFlip.cbvColormapFlip}
                onChange={(e) => changeSetting(e)}
              >
                {colorMapOption[0]}
              </Input>
              <Input
                className={'has-example'}
                type={`radio`}
                name={'CBV'}
                value={'lowRed'}
                checked={colorMapFlip.cbvColormapFlip}
                onChange={(e) => changeSetting(e)}
              >
                {colorMapOption[1]}
              </Input>
            </div>
            <div className="flex-box">
              <div className="color-map-title">MTT</div>
              <Input
                className={'has-example'}
                type={`radio`}
                name={'MTT'}
                value={'lowBlue'}
                checked={!colorMapFlip.mttColormapFlip}
                onChange={(e) => changeSetting(e)}
              >
                {colorMapOption[0]}
              </Input>
              <Input
                className={'has-example'}
                type={`radio`}
                name={'MTT'}
                value={'lowRed'}
                checked={colorMapFlip.mttColormapFlip}
                onChange={(e) => changeSetting(e)}
              >
                {colorMapOption[1]}
              </Input>
            </div>
            <div className="flex-box">
              <div className="color-map-title">TTP</div>
              <Input
                className={'has-example'}
                type={`radio`}
                name={'TTP'}
                value={'lowBlue'}
                checked={!colorMapFlip.ttpColormapFlip}
                onChange={(e) => changeSetting(e)}
              >
                {colorMapOption[0]}
              </Input>
              <Input
                className={'has-example'}
                type={`radio`}
                name={'TTP'}
                value={'lowRed'}
                checked={colorMapFlip.ttpColormapFlip}
                onChange={(e) => changeSetting(e)}
              >
                {colorMapOption[1]}
              </Input>
            </div>
          </StyledReportFlex>
        </StyledReportArea>

        <StyledReportArea>
          <strong>Duplicate check</strong>
          <StyledReportFlex>
            <StyledOnOffFlex>
              <span>Off</span>
              <div className="switch-wrap">
                <Switch
                  defaultValue={checkDuplicate}
                  onChangeValue={(next) => {
                    setData({ ...data, checkDuplicate: next });
                    setCheckDuplicate(next);
                  }}
                />
              </div>
              <span>On</span>
            </StyledOnOffFlex>
            <StyledDuplicateDescription>
              If the duplicate check is off, you can analyze the same data repeatedly.
            </StyledDuplicateDescription>
          </StyledReportFlex>
        </StyledReportArea>
      </StyledReportWrap>

      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            margin-top: 8px;
            font-size: 18px;
          `}
          onClick={submitDebounce(updateNotificationSettings)}
          disabled={!isModified}
        >
          Apply
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};

export default Option;
